import React, { useState, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from 'recharts';
import { Box, Flex, Heading, Select, Table, Thead, Tbody, Tr, Th, Td, Button, Checkbox, useColorModeValue, Text } from '@chakra-ui/react';

function formatDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString();
}

function movingAverage(arr, windowSize) {
  return arr.map((v, i, a) => {
    const start = Math.max(0, i - windowSize + 1);
    const window = a.slice(start, i + 1);
    return window.reduce((sum, x) => sum + x, 0) / window.length;
  });
}

function CustomTooltip({ active, payload, label }) {
  const tooltipBg = useColorModeValue('white', 'gray.800');
  const tooltipBorder = useColorModeValue('gray.200', 'gray.700');
  const tooltipTextColor = useColorModeValue('gray.800', 'gray.100');

  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload;

  return (
    <Box
      bg={tooltipBg}
      border="1px"
      borderColor={tooltipBorder}
      borderRadius="md"
      p={3}
      fontSize="sm"
      boxShadow="md"
      color={tooltipTextColor}
    >
      <Text fontWeight="bold" mb={1}>{d.date}</Text>
      {payload.map(pld => (
        <Text key={pld.dataKey} color={pld.stroke === '#888' ? tooltipTextColor : pld.stroke}>
          {pld.name}: <Text as="span" fontWeight="bold">{pld.value}{pld.dataKey.includes('Rate') ? '%' : ''}</Text>
        </Text>
      ))}
    </Box>
  );
}

export default function TestHistory({ onSelectRun, onCompareRuns, selectedProject, projects, runs, loading }) {
  const [selected, setSelected] = useState([]);
  const [module, setModule] = useState('All Modules');

  // Chakra color mode values (move all to top level)
  const cardBg = useColorModeValue('white', 'gray.800');
  const cardBorder = useColorModeValue('gray.200', 'gray.700');
  const tableHeaderBg = useColorModeValue('green.50', 'gray.700');
  const tableHeaderColor = useColorModeValue('green.700', 'green.200');
  const tableRowBgEven = useColorModeValue('white', 'gray.800');
  const tableRowBgOdd = useColorModeValue('gray.50', 'gray.700');
  // const tableBorder = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.800', 'gray.100');
  const accentColor = useColorModeValue('green.500', 'green.300');
  const failColor = useColorModeValue('red.500', 'red.300');
  const passColor = useColorModeValue('green.600', 'green.200');
  const skipColor = useColorModeValue('yellow.600', 'yellow.300');
  const passRateMAColor = useColorModeValue('#3b82f6', '#60a5fa');

  let filteredRuns = runs || [];
  if (selectedProject && selectedProject !== 'ALL PROJECTS') {
    filteredRuns = filteredRuns.filter(r => r.project === selectedProject);
  }

  function toggleSelect(id) {
    setSelected(sel => sel.includes(id) ? sel.filter(x => x !== id) : sel.length < 2 ? [...sel, id] : sel);
  }

  const safeRuns = filteredRuns;

  const handleCompareRuns = () => {
    if (onCompareRuns && selected.length === 2) {
      const run1 = safeRuns.find(r => r.id === selected[0]);
      const run2 = safeRuns.find(r => r.id === selected[1]);
      if (run1 && run2) {
        onCompareRuns(run1, run2); // Pass full run objects
      } else {
        console.error("Could not find selected runs for comparison. Ensure IDs are valid and runs are in safeRuns.");
        // Optionally, display an error message to the user here
      }
    }
  };

  const allModules = useMemo(() => {
    const modules = Array.from(new Set(safeRuns.flatMap(run => (run.testcases || []).map(tc => tc.classname || 'Unknown')))).sort();
    modules.unshift('All Modules');
    return modules;
  }, [safeRuns]);

  const chartData = useMemo(() => {
    let data = safeRuns.map(run => {
      let tcs = run.testcases || [];
      if (module !== 'All Modules') tcs = tcs.filter(tc => (tc.classname || 'Unknown') === module);
      
      const failures = tcs.filter(tc => tc.failure || tc.error).length;
      const skipped = tcs.filter(tc => tc.skipped !== undefined).length;
      const total = tcs.length;
      const passed = total - failures - skipped;
      const passRate = total ? (passed / total * 100) : 0;
      const flakyRate = total ? (skipped / total * 100) : 0;

      return {
        id: run.id,
        date: formatDate(run.timestamp),
        passRate: Number(passRate.toFixed(1)),
        passed: passed,
        failed: failures,
        skipped: skipped,
        total: total,
        flakyRate: Number(flakyRate.toFixed(1)),
      };
    }).reverse();

    const passRates = data.map(d => d.passRate);
    const passRateMAValues = movingAverage(passRates, 7);
    return data.map((d, i) => ({ ...d, passRateMA: Number(passRateMAValues[i].toFixed(1)) }));
  }, [safeRuns, module]);

  if (loading) return <Box p={12} color={textColor}>Loading test history...</Box>;
  if (!safeRuns.length && !loading) return <Box p={12} textAlign="center" color={textColor}>No test runs found for the selected project.</Box>;

  return (
    <Box w="100%" py={8} px={{ base: 4, md: 8 }}>
      <Heading as="h2" size="lg" mb={6} color={textColor}>Test Run History</Heading>
      <Flex mb={6} align="center" gap={4} flexWrap="wrap">
        <Text fontWeight="semibold" fontSize="md" color="gray.500">Module:</Text>
        <Select
          id="moduleFilterHistory"
          value={module}
          onChange={e => setModule(e.target.value)}
          minW="250px"
          maxW="350px"
          size="md"
          bg={cardBg}
          borderColor={cardBorder}
        >
          {allModules.map(mod => <option key={mod} value={mod}>{mod}</option>)}
        </Select>
      </Flex>
      <Box bg={cardBg} borderRadius="lg" boxShadow="md" borderWidth={1} borderColor={cardBorder} mb={8} p={{ base: 2, md: 4 }} w="100%" maxW="100%" minH="350px">
        {chartData && chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={320}>
            <LineChart data={chartData} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={cardBorder} />
              <XAxis dataKey="date" stroke={textColor} fontSize="0.95rem" />
              <YAxis yAxisId="left" stroke={textColor} fontSize="0.95rem" />
              <YAxis yAxisId="right" orientation="right" stroke={textColor} fontSize="0.95rem" />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{fontSize: "0.95rem"}} />
              <Line yAxisId="left" type="monotone" dataKey="passRate" name="Pass Rate (%)" stroke={accentColor} activeDot={{ r: 6 }} strokeWidth={2} dot={{r:3}} />
              <Line yAxisId="left" type="monotone" dataKey="passRateMA" name="Pass Rate (MA)" stroke={passRateMAColor} strokeDasharray="5 5" strokeWidth={2} dot={false}/>
              <Line yAxisId="right" type="monotone" dataKey="failed" name="Failures" stroke={failColor} activeDot={{ r: 6 }} strokeWidth={2} dot={{r:3}} />
              <Line yAxisId="right" type="monotone" dataKey="skipped" name="Skipped" stroke={skipColor} activeDot={{ r: 6 }} strokeWidth={2} dot={{r:3}} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <Flex align="center" justify="center" h="100%" color="gray.400">No chart data available for the selected module or project.</Flex>
        )}
      </Box>
      {selected.length === 2 && (
        <Button colorScheme="green" mb={6} onClick={handleCompareRuns} size="md" borderRadius="lg">Compare Selected Runs</Button>
      )}
      <Box overflowX="auto" borderRadius="lg" boxShadow="md" borderWidth={1} borderColor={cardBorder} bg={cardBg}>
        <Table variant="simple" size="md">
          <Thead bg={tableHeaderBg}>
            <Tr>
              <Th textAlign="center" w="48px"><Checkbox isDisabled opacity={0} /></Th>
              <Th color={tableHeaderColor}>Date</Th>
              <Th color={tableHeaderColor}>Total</Th>
              <Th color={passColor}>Passed</Th>
              <Th color={failColor}>Failed</Th>
              <Th color={skipColor}>Skipped</Th>
              <Th color={tableHeaderColor}>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {safeRuns.map((run, i) => {
              const summary = run.summary || {};
              const total = summary.tests ?? 0;
              const failures = summary.failures ?? 0;
              const errors = summary.errors ?? 0;
              const failedCount = failures + errors;
              const skipped = summary.skipped ?? 0;
              const passed = total - failedCount - skipped;
              return (
                <Tr key={run.id} bg={i % 2 === 0 ? tableRowBgEven : tableRowBgOdd}>
                  <Td textAlign="center" w="48px">
                    <Checkbox
                      isChecked={selected.includes(run.id)}
                      onChange={() => toggleSelect(run.id)}
                      isDisabled={selected.length === 2 && !selected.includes(run.id)}
                      aria-label="Select for compare"
                      colorScheme="green"
                      size="md"
                    />
                  </Td>
                  <Td color={textColor}>{formatDate(run.timestamp)}</Td>
                  <Td color={textColor}>{total}</Td>
                  <Td color={passColor} fontWeight="bold">{passed}</Td>
                  <Td color={failColor} fontWeight="bold">{failedCount}</Td>
                  <Td color={skipColor} fontWeight="bold">{skipped}</Td>
                  <Td>
                    <Button size="sm" colorScheme="green" borderRadius="lg" onClick={() => onSelectRun && onSelectRun(run.id)}>
                      View
                    </Button>
                  </Td>
                </Tr>
              );
            })}
            {safeRuns.length === 0 && (
              <Tr>
                <Td colSpan={7} textAlign="center" py={8} color="gray.400">No runs found for the selected project or module.</Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
} 